import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navItems } from '../../data/navigation';
import { useLocalizedPath } from '../../routes/useLanguageSync';
import { ReactComponent as Logo } from '../../assets/images/logo-horizontal.svg';
import styles from './Navbar.module.css';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const getPath = useLocalizedPath();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'hu' ? 'en' : 'hu');
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setOpenDropdown(null);
  };

  return (
    <nav className={`${styles.navbar} ${scrolled ? styles.scrolled : ''}`}>
      <div className={`container ${styles.inner}`}>
        <Link to="/" className={styles.logo} onClick={closeMobile}>
          <Logo className={styles.logoIcon} />
        </Link>

        <div className={`${styles.navLinks} ${mobileOpen ? styles.open : ''}`}>
          {navItems.map((item) => {
            const itemPath = getPath(item.routeKey, item.routeParams);
            return (
              <div key={item.routeKey} className={styles.navItem}>
                {item.children ? (
                  <>
                    <button
                      className={styles.dropdownToggle}
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === item.routeKey ? null : item.routeKey
                        )
                      }
                    >
                      {t(item.labelKey)}
                      <span className={`${styles.chevron} ${openDropdown === item.routeKey ? styles.chevronOpen : ''}`}>&#9662;</span>
                    </button>
                    <div
                      className={`${styles.dropdown} ${
                        openDropdown === item.routeKey ? styles.dropdownOpen : ''
                      }`}
                    >
                      <NavLink
                        to={itemPath}
                        end
                        className={({ isActive }) =>
                          `${styles.dropdownLink} ${isActive ? styles.active : ''}`
                        }
                        onClick={closeMobile}
                      >
                        {t(item.labelKey)}
                      </NavLink>
                      {item.children.map((child) => {
                        const childPath = getPath(child.routeKey, child.routeParams);
                        return (
                          <NavLink
                            key={childPath}
                            to={childPath}
                            className={({ isActive }) =>
                              `${styles.dropdownLink} ${isActive ? styles.active : ''}`
                            }
                            onClick={closeMobile}
                          >
                            {t(child.labelKey)}
                          </NavLink>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <NavLink
                    to={itemPath}
                    end={item.routeKey === 'home'}
                    className={({ isActive }) =>
                      `${styles.link} ${isActive ? styles.active : ''}`
                    }
                    onClick={closeMobile}
                  >
                    {t(item.labelKey)}
                  </NavLink>
                )}
              </div>
            );
          })}

          <button className={styles.langToggle} onClick={toggleLanguage}>
            {i18n.language === 'hu' ? t('language.en') : t('language.hu')}
          </button>
        </div>

        <button
          className={`${styles.hamburger} ${mobileOpen ? styles.hamburgerOpen : ''}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
